import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { parseDocs } from './parseDocs';
import {
  inscriptionCreateSchema,
  inscriptionSchema,
  type Inscription,
  type InscriptionCreate,
  type InscriptionStatus,
} from '../schemas/inscription';

const COLLECTION = 'inscriptions';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): Inscription {
  const data = raw as Record<string, unknown>;
  // `createdAt` may be a Firestore Timestamp (when written via serverTimestamp) — coerce to ISO.
  const createdAt =
    data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : (data.createdAt as string);
  const updatedAt =
    data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : (data.updatedAt as string | undefined) ?? createdAt;

  // The admin can write `null` for cleared optional profile fields
  // (faculty / otherCredentials / graduationYear / cabinet …). Those fields
  // are `.optional()` (string|undefined), NOT nullable — a stray `null` would
  // throw on parse and break the WHOLE list. Coerce profile nulls → undefined
  // so the schema accepts them (top-level nullable fields are left untouched).
  const rawProfile = data.profile;
  const profile =
    rawProfile && typeof rawProfile === 'object'
      ? Object.fromEntries(
          Object.entries(rawProfile as Record<string, unknown>).filter(([, v]) => v !== null),
        )
      : rawProfile;

  return inscriptionSchema.parse({ ...data, id, profile, createdAt, updatedAt });
}

/**
 * Public-facing create. The Firestore rule enforces shape:
 *   status='pending_contact', createdAt==request.time, profile keys present.
 *
 * If the caller is a signed-in, email-verified member submitting an inscription
 * with their own email, pass `memberUid` so the inscription is linked at
 * write time. Otherwise it's a guest inscription and stays unlinked until the
 * candidate later creates an account + verifies email (handled by the
 * `linkMemberInscriptions` Cloud Function).
 */
export async function createInscription(
  input: InscriptionCreate & { memberUid?: string | null },
): Promise<string> {
  const { memberUid, ...rest } = input;
  const parsed = inscriptionCreateSchema.parse(rest);
  // Normalise email to lowercase so member-linking (which matches on the
  // lowercased Auth token email) never misses on casing.
  const profile = { ...parsed.profile, email: parsed.profile.email.trim().toLowerCase() };
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    profile,
    status: 'pending_contact' as InscriptionStatus,
    paymentArrangement: null,
    discount: 0,
    payments: [],
    group: null,
    cohort: null,
    memberUid: memberUid ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function listInscriptions(): Promise<Inscription[]> {
  const q = query(collection(db(), COLLECTION), orderBy('createdAt', 'desc'), limit(500));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listInscriptionsByStatus(
  status: InscriptionStatus,
): Promise<Inscription[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
    limit(500),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listInscriptionsByFormation(
  formationId: string,
): Promise<Inscription[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('formationId', '==', formationId),
    orderBy('createdAt', 'desc'),
    limit(500),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function getInscription(id: string): Promise<Inscription | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

/**
 * Generic partial update — used by the admin to advance status, attach
 * group/cohort, record payments, etc. Anything not in the patch is left alone.
 */
export async function updateInscription(
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/** Back-compat alias — same as `updateInscription`, kept for older callers. */
export const updateInscriptionStatus = updateInscription;

export async function deleteInscription(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}

/**
 * Member-side read — every inscription that has been linked to this member's UID.
 * Drives the "Mes inscriptions" dashboard.
 */
export async function listInscriptionsByMember(memberUid: string): Promise<Inscription[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('memberUid', '==', memberUid),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}
